const Button = ({
  children,
  href,
  onClick,
  variant = "solid",
  className = "",
  type = "button",
  disabled = false,
}) => {
  const base = `
    transition-all duration-300 
    text-center inline-block 
    font-semibold 
    relative overflow-hidden 
    group
    disabled:opacity-50 disabled:cursor-not-allowed
    focus:outline-none focus:ring-4 focus:ring-opacity-50
  `;

  const styles = {
    solid: `
      bg-secondary text-white 
      hover:bg-primary hover:scale-105 hover:shadow-2xl
      active:scale-95
      focus:ring-secondary/50
      px-8 py-4 rounded-2xl
      shadow-lg hover:shadow-xl
      border-2 border-secondary
      transform transition-all duration-200
    `,
    solidorange: `
      bg-opprimary text-white 
      hover:bg-orange-600 hover:scale-105 hover:shadow-2xl
      active:scale-95
      focus:ring-opprimary/50
      px-8 py-4 rounded-2xl
      shadow-lg hover:shadow-xl
      border-2 border-opprimary
      transform transition-all duration-200
      relative
    `,
    addsection: `
      bg-opsecondary text-primary 
      hover:bg-opprimary hover:text-white hover:scale-105
      active:scale-95
      focus:ring-opsecondary/50
      px-4 py-2 rounded-xl
      shadow-md hover:shadow-lg
      border-2 border-opsecondary
      transform transition-all duration-200
      text-sm font-medium
    `,
    submitButton: `
      bg-secondary text-white 
      hover:bg-opprimary hover:text-white hover:scale-105
      active:scale-95
      focus:ring-secondary/50
      px-8 py-4 rounded-2xl
      shadow-lg hover:shadow-xl
      border-2 border-secondary
      transform transition-all duration-200
      font-medium
    `,
    outline: `
      bg-transparent text-primary 
      border-2 border-primary 
      hover:bg-primary hover:text-white hover:scale-105
      active:scale-95
      focus:ring-primary/50
      px-8 py-4 rounded-2xl
      shadow-lg hover:shadow-xl
      transform transition-all duration-200
    `,
    outlineorange: `
      bg-transparent text-opprimary 
      border-2 border-opprimary 
      hover:bg-opprimary hover:text-white hover:scale-105
      active:scale-95
      focus:ring-opprimary/50
      px-8 py-4 rounded-2xl
      shadow-lg hover:shadow-xl
      transform transition-all duration-200
    `,
    ghost: `
      bg-transparent text-primary 
      hover:bg-primary/10 hover:scale-105
      active:scale-95
      focus:ring-primary/30
      px-6 py-3 rounded-xl
      transform transition-all duration-200
    `
  };

  const combined = `
    ${base} 
    ${styles[variant]} 
    ${className}
    ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
  `.replace(/\s+/g, ' ').trim();

  // Ripple effect component
  const RippleEffect = () => (
    <span className="absolute inset-0 overflow-hidden rounded-2xl">
      <span className="
        absolute top-1/2 left-1/2 
        w-0 h-0 
        bg-white/20 
        rounded-full 
        group-hover:w-80 group-hover:h-80 
        group-hover:opacity-0 
        transition-all duration-700 
        transform -translate-x-1/2 -translate-y-1/2
        group-active:bg-white/30
      "></span>
    </span>
  );

  // Shine effect component
  const ShineEffect = () => (
    <span className="
      absolute top-0 left-0 
      w-full h-full 
      overflow-hidden rounded-2xl
    ">
      <span className="
        absolute top-0 left-0 
        w-10 h-full 
        bg-white/10 
        skew-x-12 
        transform -translate-x-40 
        group-hover:translate-x-96 
        transition-all duration-1000
      "></span>
    </span>
  );

  const ButtonContent = () => (
    <>
      {/* Background effects */}
      {variant.includes('solid') && <RippleEffect />}
      {variant.includes('solid') && <ShineEffect />}
      
      {/* Content with hover scale */}
      <span className="relative z-10 flex items-center justify-center gap-2">
        {children}
        {/* Arrow icon for some variants */}
        {(variant === 'solid' || variant === 'solidorange') && (
          <svg 
            className="
              w-4 h-4 
              transform group-hover:translate-x-1 
              transition-transform duration-300
            " 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
          </svg>
        )}
      </span>

      {/* Pulse animation for important buttons */}
      {(variant === 'solidorange' || variant === 'submitButton') && !disabled && (
        <span className="
          absolute -top-1 -right-1 
          w-3 h-3 
          bg-opprimary 
          rounded-full 
          animate-ping
          opacity-75
        "></span>
      )}
    </>
  );

  if (href && !disabled) {
    return (
      <a 
        href={href} 
        className={combined}
        onClick={onClick}
      >
        <ButtonContent />
      </a>
    );
  }

  return (
    <button 
      onClick={onClick} 
      className={combined} 
      type={type}
      disabled={disabled}
    >
      <ButtonContent />
    </button>
  );
};

export default Button;